import { Injectable } from '@angular/core';

export interface AddressFillResult {
  isValid: boolean;
  city: string;
  state: string;
  message: string;
  suggestions: Array<{
    city: string;
    state: string;
    displayName: string;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class AddressFillService {
  private lastValidationTime = 0;
  private readonly MIN_REQUEST_INTERVAL = 1000; // 1 second between requests

  constructor() {}

  /**
   * Validates postal code using Nominatim API and returns address details
   * @param postalCode - The postal code to validate
   * @param countryName - The full country name (e.g., 'UNITED STATES', 'INDIA')
   * @returns Promise with validation result
   */
  async validatePostalCode(
    postalCode: string,
    countryName: string
  ): Promise<AddressFillResult> {
    // Rate limiting: ensure at least 1 second between calls
    const now = Date.now();
    const timeSinceLastCall = now - this.lastValidationTime;
    
    if (timeSinceLastCall < this.MIN_REQUEST_INTERVAL) {
      await new Promise(resolve => 
        setTimeout(resolve, this.MIN_REQUEST_INTERVAL - timeSinceLastCall)
      );
    }
    
    this.lastValidationTime = Date.now();

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(postalCode)}&country=${encodeURIComponent(countryName)}&format=json&addressdetails=1&limit=5`,
        {
          headers: {
            'User-Agent': 'OneQID-AddressValidator/1.0',
            'Accept-Language': 'en'
          }
        }
      );

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const location = data[0];
        const address = location.address || {};
        
        // Extract clean city name
        let city = address.city || address.town || address.village || 
                   address.municipality || address.county || '';
        
        // Clean up city name - remove administrative subdivisions
        city = this.cleanCityName(city);
        
        // If city is still empty or too generic, try other fields
        if (!city || city.length < 2) {
          city = address.state_district || address.suburb || '';
          city = city.replace(/\s+District/gi, '').trim();
        }
        
        // Extract state/region
        const state = address.state || address.region || address.province || '';

        // Prepare suggestions if multiple results
        const suggestions = data.slice(0, 3).map((loc: any) => ({
          city: loc.address?.city || loc.address?.town || loc.address?.village || '',
          state: loc.address?.state || loc.address?.region || '',
          displayName: loc.display_name
        }));

        return {
          isValid: true,
          city: city,
          state: state,
          message: 'Valid postal code! Address details loaded.',
          suggestions: suggestions.length > 1 ? suggestions : []
        };
      } else {
        return {
          isValid: false,
          city: '',
          state: '',
          message: 'Postal code not found. Please check and try again.',
          suggestions: []
        };
      }
    } catch (error) {
      console.error('Validation error:', error);
      return {
        isValid: false,
        city: '',
        state: '',
        message: 'Unable to validate. Please try again.',
        suggestions: []
      };
    }
  }

  /**
   * Clean city name by removing administrative subdivisions
   */
  private cleanCityName(city: string): string {
    return city
      .replace(/\s+(East|West|North|South|Central)\s+Taluka/gi, '')
      .replace(/\s+Taluka/gi, '')
      .replace(/\s+District/gi, '')
      .replace(/\s+Division/gi, '')
      .replace(/\s+Sub-District/gi, '')
      .replace(/\s+Municipality/gi, '')
      .replace(/\s+Township/gi, '')
      .replace(/\s+(East|West|North|South|Central)$/gi, '')
      .trim();
  }
}
